// A CAPTURA AUTOMÁTICA: a tag passa a mandar evento sozinha.
//
// Medido em 02/10/2026: `autoIniciar` montava o Tracker, chamava `personalizar()` e **nunca chamava
// `track`**. Uma página com a tag instalada enviava ZERO eventos, a menos que o site chamasse
// `synqi3.track(...)` por conta própria. Ou seja, o produto prometia medição de comportamento e
// entregava uma superfície para o cliente programar.
//
// ## O que é capturado, e por quê cada um
//
//     session_start   uma vez por sessão, com o contexto INTEIRO (navegador, sistema, tela, origem)
//     page_view       na entrada e em cada navegação de SPA
//     scroll_depth    aos 25/50/75/100%, uma vez cada                 -> leu ou só abriu?
//     page_leave      ao sair, com segundos na página e rolagem máxima -> permanência real
//     click           em link, botão e `[data-synqi3-click]`           -> o que foi tocado
//     form_start      no primeiro foco dentro de um formulário         -> começou e desistiu?
//     form_submit     no envio, com os NOMES dos campos                -> qual formulário converteu
//
// ## O que NÃO é capturado, e isto é desenho e não falta
//
// **Nenhum valor digitado.** `form_submit` leva os NOMES dos campos e nunca o conteúdo: o nome diz qual
// formulário é, o valor é o dado da pessoa e não tem o que fazer num evento de comportamento. Quem
// precisa do valor manda `conversion()` ou `identify()` de propósito, sabendo o que está mandando.
//
// **Nada de impressão digital.** Ver `contexto.js`.
//
// **Clique em qualquer elemento, não.** Só link, botão, `role=button` e o que o site marcar com
// `data-synqi3-click`. Capturar todo clique do documento enche a série de ruído e aumenta a chance de
// arrastar texto que não devia — e ruído é como uma medição morre.
//
// ## O Global Privacy Control é respeitado por padrão
//
// Com `navigator.globalPrivacyControl` ligado, a captura cai para `page_view` e nada mais: sem
// comportamento, sem contexto de sessão, sem atribuição persistida. É o sinal com peso legal, e um
// inquilino com base própria o dispensa com `data-synqi3-ignore-gpc`. O padrão é respeitar porque o
// risco de ignorar é do DONO DA PÁGINA, e não nosso.

import { contextoCompacto, contextoDaSessao, pediuParaNaoRastrear } from "./contexto.js";

//: Os marcos de rolagem, e os cinco vêm do DADO, não da minha escolha.
//:
//: Eu havia escrito `[25, 50, 75, 100]`. Medido em 02/10/2026 no lake, nos cinco tenants que mandam
//: `scroll_depth`: TODOS usam `[25, 50, 75, 90, 100]`, e o `fabrik7` tem 2.020 eventos no marco 90.
//: Conjunto diferente para a mesma pergunta não é detalhe: um painel que compare os dois caminhos vê
//: um balde `90` que existe num e não no outro, e a taxa de leitura muda de resposta conforme quem
//: mediu. O `Tracker` já declarava os cinco em `marcosDeScroll`, e é de lá que estes saem quando ele
//: existe — UMA declaração, com esta constante só como piso para quem chama `ligarCaptura` sem tracker.
export const MARCOS_DE_ROLAGEM = [25, 50, 75, 90, 100];

/** Limite do texto de um clique. Rótulo de botão é identificação; parágrafo é conteúdo alheio. */
export const MAX_TEXTO = 40;

/** O identificador estável de um elemento, na ordem do mais declarado para o menos. */
export function identificadorDe(el) {
  if (!el) return undefined;
  const d = el.dataset || {};
  if (d.synqi3Click) return String(d.synqi3Click);
  if (el.id) return `#${el.id}`;
  if (el.getAttribute && el.getAttribute("name")) return `[name=${el.getAttribute("name")}]`;
  if (el.getAttribute && el.getAttribute("aria-label")) return String(el.getAttribute("aria-label")).slice(0, MAX_TEXTO);
  const cls = typeof el.className === "string" ? el.className.trim().split(/\s+/)[0] : "";
  return cls ? `.${cls}` : (el.tagName || "").toLowerCase() || undefined;
}

/** O alvo clicável mais próximo, subindo no máximo 4 níveis. `null` quando não há. */
export function alvoClicavel(el, limite = 4) {
  let n = el, i = 0;
  while (n && i++ <= limite) {
    const tag = (n.tagName || "").toLowerCase();
    const papel = n.getAttribute && n.getAttribute("role");
    if (tag === "a" || tag === "button" || papel === "button"
        || (n.dataset && n.dataset.synqi3Click)) return n;
    n = n.parentElement;
  }
  return null;
}

/** Os NOMES dos campos de um formulário. Nunca os valores. */
export function camposDe(form) {
  const fora = [];
  const els = (form && form.elements) || [];
  for (const el of els) {
    const tipo = (el.type || "").toLowerCase();
    if (tipo === "hidden" || tipo === "submit" || tipo === "button") continue;
    const nome = el.name || el.id;
    if (nome && !fora.includes(nome)) fora.push(nome);
  }
  return fora;
}

/** A porcentagem rolada agora, 0..100. `0` quando a página não rola. */
export function rolagemAtual({ win, doc }) {
  try {
    const alturaDoc = Math.max(
      doc.body ? doc.body.scrollHeight : 0, doc.documentElement ? doc.documentElement.scrollHeight : 0);
    const vista = win.innerHeight || 0;
    const rolavel = alturaDoc - vista;
    if (rolavel <= 0) return 100;              // cabe na tela: foi vista inteira, e isso é 100
    const y = win.scrollY || (doc.documentElement ? doc.documentElement.scrollTop : 0) || 0;
    return Math.max(0, Math.min(100, Math.round((y / rolavel) * 100)));
  } catch {
    return 0;
  }
}

/**
 * Liga a captura automática num tracker. Devolve `{ parar() }`.
 *
 * Tudo injetável (`win`, `doc`, `nav`, `scr`), pelo mesmo motivo do resto do SDK: teste não abre janela.
 * `NUNCA levanta` — um erro aqui roda dentro da página de um cliente.
 */
export function ligarCaptura(tracker, {
  win = typeof window !== "undefined" ? window : null,
  doc = typeof document !== "undefined" ? document : null,
  nav = typeof navigator !== "undefined" ? navigator : null,
  scr = typeof screen !== "undefined" ? screen : null,
  ignorarGpc = false,
  comportamento = true,
} = {}) {
  const limpar = [];
  const parar = () => { for (const f of limpar) { try { f(); } catch { /* nada */ } } limpar.length = 0; };
  if (!win || !doc) return { parar, ativo: false, reason: "sem_dom" };

  const silencio = !ignorarGpc && pediuParaNaoRastrear({ nav });
  const medirComportamento = comportamento && !silencio;

  const on = (alvo, evento, fn, opts) => {
    try {
      alvo.addEventListener(evento, fn, opts);
      limpar.push(() => alvo.removeEventListener(evento, fn, opts));
    } catch { /* alvo sem addEventListener: segue sem esse sinal */ }
  };

  // ---- session_start: o contexto inteiro, uma vez ----
  if (!silencio && tracker.sessaoNova) {
    const ctx = contextoDaSessao({ nav, loc: win.location, doc, scr, win });
    tracker.contextoDaVisita = ctx;
    try {
      tracker.track("session_start", {
        ...ctx,
        reason: tracker.sessaoMotivo,
        // `first_visit` só entra quando houve onde guardá-lo: sem storage, afirmar que a primeira
        // visita foi agora tornaria todo visitante um novato a cada carregamento.
        ...(tracker.primeiraVisita ? { first_visit: tracker.primeiraVisita } : {}),
      });
    } catch { /* nada */ }
  } else if (!silencio) {
    // Sessão que continua: o compacto ainda precisa existir para enriquecer os eventos.
    tracker.contextoDaVisita = contextoDaSessao({ nav, loc: win.location, doc, scr, win });
  }

  // ---- page_view, na entrada e em cada navegação ----
  let caminhoAtual = null;
  let abertaEm = Date.now();
  let marcosVistos = new Set();
  let rolagemMax = 0;
  //: O instante da ÚLTIMA despedida desta página, e se ela já se despediu estando oculta.
  //:
  //: Medido em 02/10/2026 com o bundle publicado: `page_leave` saía DUAS vezes na saída. Não era o
  //: dublê — num navegador de verdade `visibilitychange: hidden` e `pagehide` disparam os dois, nessa
  //: ordem, e eu tinha um ouvinte em cada. Permanência dobrada.
  //:
  //: `desde` é o que faz `seconds` ser um DELTA, e não o acumulado: quem esconde a aba aos 10 s,
  //: volta e sai aos 300 s produz dois `page_leave` de 10 e de 290, e a soma dá os 300 certos. É a
  //: semântica do `user_engagement` do GA4 (`engagement_time_msec` é delta), escolhida porque é o que
  //: torna o número comparável com o que o inquilino já mede noutra ferramenta. Com acumulado, somar
  //: daria 310 e nenhum consumidor teria como saber que precisava tirar o máximo em vez da soma.
  let desde = abertaEm;
  let jaSeDespediu = false;

  const paginaVista = () => {
    const p = win.location ? win.location.pathname : undefined;
    if (p === caminhoAtual) return;                 // `replaceState` repetido não é página nova
    if (caminhoAtual !== null) despedida("navigation");
    caminhoAtual = p;
    abertaEm = Date.now();
    desde = abertaEm;
    jaSeDespediu = false;
    marcosVistos = new Set();
    rolagemMax = 0;
    try { tracker.trackPageView(); } catch { /* nada */ }
  };

  const despedida = (razao) => {
    if (!medirComportamento || caminhoAtual === null) return;
    const agora = Date.now();
    try {
      tracker.track("page_leave", {
        path: caminhoAtual,
        // DELTA desde a última despedida desta página — ver `desde`, acima. Somável.
        seconds: Math.round((agora - desde) / 1000),
        max_scroll_percent: rolagemMax,
        reason: razao,
      });
    } catch { /* nada */ }
    desde = agora;
  };

  paginaVista();

  // SPA: `pushState`/`replaceState` não disparam evento, então são embrulhados. O original é restaurado
  // no `parar()` — embrulho que não se desfaz é vazamento na página de outra pessoa.
  for (const nome of ["pushState", "replaceState"]) {
    try {
      const hist = win.history;
      if (!hist || typeof hist[nome] !== "function") continue;
      const original = hist[nome];
      hist[nome] = function (...args) {
        const r = original.apply(this, args);
        try { paginaVista(); } catch { /* nada */ }
        return r;
      };
      limpar.push(() => { try { hist[nome] = original; } catch { /* nada */ } });
    } catch { /* nada */ }
  }
  on(win, "popstate", () => paginaVista());
  on(win, "hashchange", () => paginaVista());

  if (medirComportamento) {
    // ---- rolagem ----
    // UM mecanismo só. O `Tracker` tem o ouvinte dele (`_ouvirScroll`, ligado no construtor por
    // padrão); este aqui é mais completo (carrega `path` e acumula `max_scroll_percent` para o
    // `page_leave`), então ele assume e o outro se desliga. Medido antes de desligar: 5 eventos de
    // `scroll_depth` para UMA rolagem.
    if (typeof tracker.pararRolagemPropria === "function") tracker.pararRolagemPropria();
    // UMA declaração dos marcos: a do tracker quando ela existe.
    const marcos = Array.isArray(tracker.marcosDeScroll) && tracker.marcosDeScroll.length
      ? tracker.marcosDeScroll
      : MARCOS_DE_ROLAGEM;
    on(win, "scroll", () => {
      const pct = rolagemAtual({ win, doc });
      if (pct > rolagemMax) rolagemMax = pct;
      for (const m of marcos) {
        if (pct >= m && !marcosVistos.has(m)) {
          marcosVistos.add(m);
          try { tracker.track("scroll_depth", { path: caminhoAtual, percent: m }); } catch { /* nada */ }
        }
      }
    }, { passive: true });

    // ---- clique ----
    on(doc, "click", (e) => {
      const alvo = alvoClicavel(e.target);
      if (!alvo) return;
      const props = { path: caminhoAtual, tag: (alvo.tagName || "").toLowerCase() };
      const id = identificadorDe(alvo);
      if (id) props.element_id = id;
      const texto = (alvo.textContent || "").trim().replace(/\s+/g, " ").slice(0, MAX_TEXTO);
      if (texto) props.label = texto;
      const href = alvo.getAttribute && alvo.getAttribute("href");
      if (href) {
        props.href = href.slice(0, 200);
        try {
          const h = new URL(href, win.location && win.location.href).hostname;
          if (h && win.location && h !== win.location.hostname) props.href_host = h;
        } catch { /* href relativo ou inválido: fica só o destino cru */ }
      }
      try { tracker.track("click", props); } catch { /* nada */ }
    }, true);

    // ---- formulário: começou, e enviou ----
    const comecados = new WeakSet();
    on(doc, "focusin", (e) => {
      const form = e.target && e.target.form;
      if (!form || comecados.has(form)) return;
      comecados.add(form);
      try {
        tracker.track("form_start", {
          path: caminhoAtual, form: identificadorDe(form), fields: camposDe(form).join(","),
        });
      } catch { /* nada */ }
    }, true);
    on(doc, "submit", (e) => {
      const form = e.target;
      if (!form || !form.elements) return;
      try {
        tracker.track("form_submit", {
          path: caminhoAtual, form: identificadorDe(form), fields: camposDe(form).join(","),
          seconds: Math.round((Date.now() - abertaEm) / 1000),
        });
      } catch { /* nada */ }
    }, true);

    // ---- saída ----
    //
    // DOIS sinais, UMA despedida. `visibilitychange: hidden` é o último momento confiável para
    // mandar (num celular a aba pode ser morta sem `pagehide` nenhum), e o `pagehide` é o que existe
    // no desktop. Os dois disparam na saída de verdade, nessa ordem — então o segundo só emite se o
    // primeiro não emitiu. O travamento se desarma quando a página volta a ser vista, e aí a próxima
    // despedida carrega só o tempo novo.
    const despedirUmaVez = (razao) => {
      if (jaSeDespediu) return;
      jaSeDespediu = true;
      despedida(razao);
    };
    on(doc, "visibilitychange", () => {
      if (doc.visibilityState === "hidden") despedirUmaVez("hidden");
      else jaSeDespediu = false;                  // voltou: a próxima saída conta a partir de agora
    });
    on(win, "pagehide", () => despedirUmaVez("pagehide"));
  }

  return { parar, ativo: true, silencio, comportamento: medirComportamento };
}
